
import { useMemo, useState } from 'react';
import { useGetProcurements } from '@/hooks/use-procurement';
import { Procurement } from '@/types/database.types';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

export type SortConfig = { key: string; direction: 'asc' | 'desc' };
export type ProcurementStatus = 
  | 'pending_finance' 
  | 'pending'
  | 'initiated'
  | 'awaiting_approval'
  | 'price_confirmed' 
  | 'quality_confirmed' 
  | 'agreement_signed' 
  | 'pickup_scheduled' 
  | 'harvested' 
  | 'in_transit' 
  | 'delivered' 
  | 'completed'
  | 'rejected';

export type FilterDateRange = {
  from: Date | undefined;
  to: Date | undefined;
};

export type FilterOptions = {
  dateRange: FilterDateRange;
  paymentTermType: string | null;
  sourceType: string | null;
  sourceId: string | null;
  productName: string | null;
};

const defaultFilterOptions: FilterOptions = {
  dateRange: { from: undefined, to: undefined },
  paymentTermType: null,
  sourceType: null,
  sourceId: null,
  productName: null
};

interface UseFinanceProcurementsByStatusProps {
  status: ProcurementStatus | ProcurementStatus[];
  defaultSortConfig?: SortConfig;
}

// Hook to fetch source names based on source type
export const useSourceNames = (sourceType: string | null) => {
  return useQuery({
    queryKey: ['sourceNames', sourceType],
    queryFn: async () => {
      if (!sourceType) return [];
      
      let data;
      switch (sourceType) {
        case 'supplier':
          const { data: suppliers } = await supabase
            .from('suppliers')
            .select('id, name')
            .eq('status', 'active');
          data = suppliers || [];
          break;
        case 'agent':
          const { data: agents } = await supabase
            .from('agents')
            .select('id, name')
            .eq('status', 'active');
          data = agents || [];
          break;
        case 'farm':
          const { data: farms } = await supabase
            .from('farms')
            .select('id, farmer_name as name');
          data = farms || [];
          break;
        default:
          data = [];
      }
      
      return data;
    },
    enabled: !!sourceType && sourceType !== 'harvest'
  });
};

export const useFinanceProcurementsByStatus = ({
  status,
  defaultSortConfig = { key: 'created_at', direction: 'desc' }
}: UseFinanceProcurementsByStatusProps) => {
  const { data: procurements, isLoading } = useGetProcurements();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSortConfig);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(defaultFilterOptions);

  const filteredProcurements = useMemo(() => {
    if (!procurements) return [];
    
    // Convert status to array if it's not already
    const statusArray = Array.isArray(status) ? status : [status];
    
    // Type assertion to ensure we're working with properly typed data
    let filtered = (procurements as Procurement[]).filter(p => 
      statusArray.includes(p.status as ProcurementStatus)
    );
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        (p.product_name && p.product_name.toLowerCase().includes(term)) ||
        (p.source_type && p.source_type.toLowerCase().includes(term)) ||
        (p.id && p.id.toLowerCase().includes(term))
      );
    }
    
    // Apply date range filter
    if (filterOptions.dateRange.from || filterOptions.dateRange.to) {
      filtered = filtered.filter(p => {
        const procurementDate = parseISO(p.created_at);
        const fromDate = filterOptions.dateRange.from;
        const toDate = filterOptions.dateRange.to;
        
        if (fromDate && toDate) {
          return isAfter(procurementDate, fromDate) && isBefore(procurementDate, toDate);
        } else if (fromDate) {
          return isAfter(procurementDate, fromDate);
        } else if (toDate) {
          return isBefore(procurementDate, toDate);
        }
        
        return true;
      });
    }
    
    // Apply payment term type filter
    if (filterOptions.paymentTermType) {
      filtered = filtered.filter(p => p.finance?.payment_term_type === filterOptions.paymentTermType);
    }
    
    // Apply source type filter
    if (filterOptions.sourceType) {
      filtered = filtered.filter(p => p.source_type === filterOptions.sourceType);
    }
    
    // Apply source ID filter
    if (filterOptions.sourceId) {
      filtered = filtered.filter(p => p.source_id === filterOptions.sourceId);
    }
    
    // Apply product name filter
    if (filterOptions.productName) {
      filtered = filtered.filter(p => p.product_name === filterOptions.productName);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig.key === 'total_amount') {
        const amountA = a.total_amount || 0;
        const amountB = b.total_amount || 0;
        return sortConfig.direction === 'asc' ? amountA - amountB : amountB - amountA;
      }
      
      if (sortConfig.key === 'payment_due_date') {
        // Handle null payment due dates by placing them at the end
        if (!a.finance?.payment_due_date && !b.finance?.payment_due_date) return 0;
        if (!a.finance?.payment_due_date) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!b.finance?.payment_due_date) return sortConfig.direction === 'asc' ? -1 : 1;
        
        const dateA = new Date(a.finance.payment_due_date).getTime();
        const dateB = new Date(b.finance.payment_due_date).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig.key === 'product_name') {
        const nameA = a.product_name || '';
        const nameB = b.product_name || '';
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      
      return 0;
    });
    
    return filtered;
  }, [procurements, status, searchTerm, sortConfig, filterOptions]);

  // Get unique payment term types, source types, and product names for filters
  const filterOptions_paymentTermTypes = useMemo(() => {
    if (!procurements) return [];
    
    const termTypes = new Set<string>();
    (procurements as Procurement[]).forEach(p => {
      if (p.finance?.payment_term_type) termTypes.add(p.finance.payment_term_type);
    });
    
    return Array.from(termTypes);
  }, [procurements]);
  
  const filterOptions_sourceTypes = useMemo(() => {
    if (!procurements) return [];
    
    const sourceTypes = new Set<string>();
    (procurements as Procurement[]).forEach(p => {
      if (p.source_type) sourceTypes.add(p.source_type);
    });
    
    return Array.from(sourceTypes);
  }, [procurements]);
  
  const filterOptions_productNames = useMemo(() => {
    if (!procurements) return [];
    
    const productNames = new Set<string>();
    (procurements as Procurement[]).forEach(p => {
      if (p.product_name) productNames.add(p.product_name);
    });
    
    return Array.from(productNames);
  }, [procurements]);

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const resetFilters = () => {
    setFilterOptions(defaultFilterOptions);
  };

  return {
    procurements: filteredProcurements,
    isLoading,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    filterOptions,
    setFilterOptions,
    resetFilters,
    filterOptions_paymentTermTypes,
    filterOptions_sourceTypes,
    filterOptions_productNames
  };
};
